import { useMemo, useState } from 'react';
import { Plus, Trash2, ExternalLink, Pencil } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/tables/DataTable';
import { EditSheet } from '@/components/layout/EditSheet';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAdminProjects, useUpsertProject, useDeleteProject, type ProjectRow } from '@/lib/admin-queries';
import { ProjectEditForm } from '@/features/projects/ProjectEditForm';
import { formatDate } from '@/lib/utils';
import { toast } from '@/components/ui/Toast';

const EMPTY: ProjectRow = {
  slug: '',
  title: '',
  summary: '',
  description_md: '',
  problem: '',
  approach: '',
  client: '',
  year: new Date().getFullYear(),
  status: 'shipped',
  category: 'Web',
  tech_stack: [],
  hero_media_id: null,
  business_impact: [],
  cta_label: '',
  cta_href: '',
  is_featured: false,
  display_order: 0,
  status_publish: 'draft',
  published_at: null,
};

export function ProjectsPage() {
  const { data: rows = [], isLoading } = useAdminProjects();
  const upsert = useUpsertProject();
  const remove = useDeleteProject();
  const [editing, setEditing] = useState<ProjectRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<ProjectRow | null>(null);

  const columns = useMemo(
    () => [
      {
        key: 'title',
        header: 'Title',
        cell: (r: ProjectRow) => (
          <div>
            <p className="font-medium">{r.title}</p>
            <p className="text-xs text-fg-subtle">{r.slug}</p>
          </div>
        ),
      },
      {
        key: 'category',
        header: 'Category',
        cell: (r: ProjectRow) => <Badge variant="gold">{r.category}</Badge>,
      },
      {
        key: 'status',
        header: 'Status',
        cell: (r: ProjectRow) => <Badge>{r.status}</Badge>,
      },
      {
        key: 'is_featured',
        header: 'Featured',
        cell: (r: ProjectRow) => (r.is_featured ? <Badge variant="gold">Featured</Badge> : <span className="text-fg-subtle">—</span>),
      },
      {
        key: 'updated',
        header: 'Updated',
        cell: (r: ProjectRow) => (
          <span className="text-xs text-fg-muted">{r.published_at ? formatDate(r.published_at) : '—'}</span>
        ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right' as const,
        cell: (r: ProjectRow) => (
          <div className="flex items-center justify-end gap-1">
            {r.status_publish === 'published' && (
              <a
                href={`https://vonlof.co/projects/${r.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View live"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            <button
              type="button"
              aria-label="Edit"
              onClick={(e) => {
                e.stopPropagation();
                setEditing(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleting(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-danger/15 hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  async function onSave(row: ProjectRow) {
    try {
      await upsert.mutateAsync(row);
      toast.success(row.id ? 'Project updated' : 'Project created');
      setEditing(null);
      setCreating(false);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onConfirmDelete() {
    if (!deleting?.id) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success('Project deleted');
      setDeleting(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Projects</p>
          <h1 className="mt-2 display-serif text-display-l text-fg">All projects</h1>
          <p className="mt-2 text-sm text-fg-muted">Edit case studies, gallery, tech stack, and impact metrics.</p>
        </div>
        <Button
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => {
            setEditing({ ...EMPTY });
            setCreating(true);
          }}
        >
          New project
        </Button>
      </header>

      {isLoading ? (
        <Card>
          <div className="space-y-3 p-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id ?? r.slug}
          onRowClick={(r) => setEditing(r)}
          empty="No projects yet. Click New project to add one."
        />
      )}

      <EditSheet
        open={editing !== null}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            setCreating(false);
          }
        }}
        title={creating ? 'New project' : 'Edit project'}
        description="Changes save on submit."
        size="xl"
      >
        {editing && (
          <ProjectEditForm
            value={editing}
            submitting={upsert.isPending}
            onSubmit={onSave}
            onCancel={() => {
              setEditing(null);
              setCreating(false);
            }}
          />
        )}
      </EditSheet>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this project?"
        description={
          deleting
            ? `"${deleting.title}" will be removed from the marketing site. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete project"
        destructive
        loading={remove.isPending}
        onConfirm={onConfirmDelete}
      />
    </div>
  );
}